// src/components/OpeningStock.jsx
// One-time (or anytime) entry of existing stock when starting to use the app

import { useState } from "react";
import { OILS } from "../constants";
import { fmt, fmtL, fmtKg, OilDot, AlertBar, S } from "../utils/ui";

export function OpeningStock({ state, actions, user }) {
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeOil, setActiveOil] = useState(OILS[0]);

  // Local state for all opening stock values
  const [stock, setStock] = useState(() => {
    const init = {};
    OILS.forEach(o => {
      init[o] = { rawKg: "", oilPlantL: "", oilShopL: "", cakeKg: "" };
    });
    return init;
  });

  const update = (oil, field, val) => {
    setStock(prev => ({
      ...prev,
      [oil]: { ...prev[oil], [field]: val }
    }));
  };

  const save = async () => {
    setSaving(true);
    // Build opening stock entries for all oils that have values
    const entries = [];
    for (const oil of OILS) {
      const s = stock[oil];
      if (Number(s.rawKg) > 0 || Number(s.oilPlantL) > 0 ||
          Number(s.oilShopL) > 0 || Number(s.cakeKg) > 0) {
        entries.push({
          oil,
          rawKg:     Number(s.rawKg)     || 0,
          oilPlantL: Number(s.oilPlantL) || 0,
          oilShopL:  Number(s.oilShopL)  || 0,
          cakeKg:    Number(s.cakeKg)    || 0,
        });
      }
    }
    await actions.addOpeningStock(entries, user);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const totalRawVal  = OILS.reduce((a, o) => a + (Number(stock[o].rawKg)     || 0) * (state.prices.raw[o]  || 0), 0);
  const totalOilVal  = OILS.reduce((a, o) => a + (Number(stock[o].oilPlantL) + Number(stock[o].oilShopL) || 0) * (state.prices.oil?.[o]?.["1L"] || 0), 0);
  const totalCakeVal = OILS.reduce((a, o) => a + (Number(stock[o].cakeKg)    || 0) * (state.prices.cake[o] || 0), 0);

  return (
    <div style={{ padding: "12px 14px" }}>
      <AlertBar
        text="Use this to enter stock you already have before you started using this app. This adds to your current stock."
        type="info"
      />

      {/* Summary box */}
      {(totalRawVal + totalOilVal + totalCakeVal) > 0 && (
        <div style={{
          background: "#F0F9E8", borderRadius: 10, padding: "12px 14px",
          marginBottom: 12, border: "1px solid #C0DD97",
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#27500A", marginBottom: 8 }}>
            OPENING STOCK SUMMARY
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 12 }}>
            <div>
              <div style={{ color: "#888" }}>Raw material</div>
              <strong>{fmt(totalRawVal)}</strong>
            </div>
            <div>
              <div style={{ color: "#888" }}>Oil</div>
              <strong>{fmt(totalOilVal)}</strong>
            </div>
            <div>
              <div style={{ color: "#888" }}>Cake</div>
              <strong>{fmt(totalCakeVal)}</strong>
            </div>
          </div>
          <div style={{ marginTop: 8, borderTop: "1px solid #C0DD97", paddingTop: 8, fontSize: 13, fontWeight: 700, color: "#27500A" }}>
            Total opening value: {fmt(totalRawVal + totalOilVal + totalCakeVal)}
          </div>
        </div>
      )}

      {/* Oil selector */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {OILS.map(o => {
          const hasData = Object.values(stock[o]).some(v => Number(v) > 0);
          return (
            <div key={o} onClick={() => setActiveOil(o)}
              style={{
                padding: "6px 12px", borderRadius: 8, fontSize: 12,
                cursor: "pointer",
                background: activeOil === o ? "#27500A" : hasData ? "#EAF3DE" : "#F7F4EE",
                color: activeOil === o ? "#C0DD97" : hasData ? "#27500A" : "#555",
                border: `1px solid ${activeOil === o ? "#27500A" : hasData ? "#C0DD97" : "#E0DDD5"}`,
                fontWeight: hasData ? 600 : 400,
              }}>
              <OilDot oil={o} size={6} />{o}
              {hasData && " ✓"}
            </div>
          );
        })}
      </div>

      {/* Entry form for active oil */}
      <div style={S.card}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <OilDot oil={activeOil} size={10} />
          <strong style={{ fontSize: 15 }}>{activeOil}</strong>
          <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>— enter existing stock</span>
        </div>

        <label style={S.label}>Raw material at plant (kg)</label>
        <input style={S.input} type="number" placeholder="0"
          value={stock[activeOil].rawKg}
          onChange={e => update(activeOil, "rawKg", e.target.value)} />

        <label style={S.label}>Oil at plant (litres)</label>
        <input style={S.input} type="number" placeholder="0"
          value={stock[activeOil].oilPlantL}
          onChange={e => update(activeOil, "oilPlantL", e.target.value)} />

        <label style={S.label}>Oil at shop (litres)</label>
        <input style={S.input} type="number" placeholder="0"
          value={stock[activeOil].oilShopL}
          onChange={e => update(activeOil, "oilShopL", e.target.value)} />

        <label style={S.label}>Cake at plant (kg)</label>
        <input style={S.input} type="number" placeholder="0"
          value={stock[activeOil].cakeKg}
          onChange={e => update(activeOil, "cakeKg", e.target.value)} />

        {/* Show value for this oil */}
        {(Number(stock[activeOil].rawKg) > 0 || Number(stock[activeOil].oilPlantL) > 0 ||
          Number(stock[activeOil].oilShopL) > 0 || Number(stock[activeOil].cakeKg) > 0) && (
          <div style={{ background: "#F7F4EE", borderRadius: 8, padding: "10px 12px", fontSize: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {Number(stock[activeOil].rawKg) > 0 && (
                <div>
                  <div style={{ color: "#888" }}>Raw value</div>
                  <strong>{fmt(Number(stock[activeOil].rawKg) * (state.prices.raw[activeOil] || 0))}</strong>
                </div>
              )}
              {(Number(stock[activeOil].oilPlantL) + Number(stock[activeOil].oilShopL)) > 0 && (
                <div>
                  <div style={{ color: "#888" }}>Oil value</div>
                  <strong>{fmt((Number(stock[activeOil].oilPlantL) + Number(stock[activeOil].oilShopL)) * (state.prices.oil?.[activeOil]?.["1L"] || 0))}</strong>
                </div>
              )}
              {Number(stock[activeOil].cakeKg) > 0 && (
                <div>
                  <div style={{ color: "#888" }}>Cake value</div>
                  <strong>{fmt(Number(stock[activeOil].cakeKg) * (state.prices.cake[activeOil] || 0))}</strong>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ fontSize: 12, color: "#888", marginBottom: 10, textAlign: "center" }}>
        Fill in all oils above, then tap Save All Opening Stock
      </div>

      {saved && (
        <div style={{ ...S.infoBox, background: "#EAF3DE", color: "#27500A", textAlign: "center", marginBottom: 10 }}>
          ✓ Opening stock saved! Dashboard is now updated.
        </div>
      )}

      <button style={S.btn(saving ? "#888" : "#27500A")} onClick={save} disabled={saving}>
        {saving ? "Saving..." : "Save All Opening Stock"}
      </button>
    </div>
  );
}
